import { useNavigate, useParams, useRouterState } from '@tanstack/react-router'
import { useState } from 'react'
import {
  type CrewWithSupplier,
  dayMinutes,
  photoOverview,
  photoRoleLabels,
  removeCrewMember,
} from '@/mock/photo'
import { useUpdate, useWedding } from '@/mock/store'
import type { PhotoRole } from '@/mock/types'
import { ConfirmDialog } from '@/patterns/dialog'
import { Screen } from '@/patterns/screen'
import { SectionTabs } from '@/patterns/section-tabs'
import { useToast } from '@/shell/toaster'
import photoIllustration from './assets/photo.svg'
import { PhotoBriefPage } from './brief'
import { PhotoCrew } from './crew'
import { CrewSheet } from './crew-sheet'
import { PhotoGroupsPage } from './groups'
import { PhotoGroupsCard, PhotoWishes } from './overview-cards'
import { PhotoBriefCard, PhotoBudgetNote, PhotoPrompts, PhotoTasks } from './preparations'

const newPrefix = 'new-'
const roles = Object.keys(photoRoleLabels) as PhotoRole[]

export function PhotoScreen() {
  const data = useWedding()
  const update = useUpdate()
  const navigate = useNavigate()
  const { showToast } = useToast()
  const { crewId } = useParams({ strict: false })
  const { crew, groups } = photoOverview(data)
  const pathname = useRouterState({ select: (state) => state.location.pathname })
  const page = pathname.startsWith('/photo/groups')
    ? '/photo/groups'
    : pathname.startsWith('/photo/brief')
      ? '/photo/brief'
      : '/photo'
  const preview = (title: string, description?: string) => showToast({ title, description })
  const openCrew = (id: string) => void navigate({ to: `/photo/crew/${id}` })
  const closeSheet = () => void navigate({ to: '/photo' })
  const [removing, setRemoving] = useState<CrewWithSupplier | null>(null)
  const removeNow = (member: CrewWithSupplier) => {
    const previous = data
    update((current) => removeCrewMember(current, member.id))
    showToast({
      title: 'Odebráno z foto a videa',
      description: member.supplier?.name ?? photoRoleLabels[member.role],
      action: {
        label: 'Vrátit zpět',
        altText: 'Vrátit odebranou roli',
        onClick: () => update(() => previous),
      },
    })
  }
  const removingLabel = removing?.supplier?.name ?? (removing ? photoRoleLabels[removing.role] : '')
  const newRole = roles.find((role) => `${newPrefix}${role}` === crewId)
  const editing = crew.find((member) => member.id === crewId)
  const groupEvent = data.schedule.find((event) => event.id === 's-groups' && !event.removed)
  const groupSlot =
    groupEvent?.time && groupEvent.endTime
      ? dayMinutes(groupEvent.endTime) - dayMinutes(groupEvent.time)
      : null

  return (
    <Screen
      eyebrow="Plánovač"
      title="Foto a video"
      description="Kdo fotí a natáčí, od kdy do kdy je s vámi a co je pro vás důležité."
      illustration={
        <img src={photoIllustration} alt="" width={128} height={112} className="h-auto w-full" />
      }
      tabs={
        <SectionTabs
          label="Stránky foto a videa"
          active={page}
          tabs={[
            { to: '/photo', label: 'Přehled' },
            { to: '/photo/groups', label: 'Skupinové fotky' },
            { to: '/photo/brief', label: 'Pro fotografa' },
          ]}
        />
      }
    >
      {page === '/photo/groups' ? (
        <PhotoGroupsPage onPreview={preview} />
      ) : page === '/photo/brief' ? (
        <PhotoBriefPage onPreview={preview} />
      ) : (
        <>
          <div className="grid items-start gap-6 xl:grid-cols-[minmax(0,1.35fr)_minmax(0,1fr)]">
            <div className="space-y-6">
              <PhotoCrew
                crew={crew}
                onOpen={(member) => openCrew(member.id)}
                onAdd={(role) => openCrew(`${newPrefix}${role}`)}
                onRemove={(member) => (member.supplier ? setRemoving(member) : removeNow(member))}
              />
              <PhotoWishes wishes={data.photo.wishes} onPreview={preview} />
            </div>
            <div className="space-y-6">
              <PhotoGroupsCard groups={groups} slotMinutes={groupSlot} />
              <PhotoBriefCard
                hasPhotographer={crew.some((member) => member.role === 'photo' && member.supplier)}
              />
            </div>
          </div>
          <PhotoPrompts crew={crew} onAddPhotographer={() => openCrew(`${newPrefix}photo`)} />
          <PhotoTasks />
          <PhotoBudgetNote />
        </>
      )}
      {editing || newRole ? (
        <CrewSheet
          key={crewId}
          crewId={editing?.id ?? null}
          role={editing?.role ?? newRole ?? 'photo'}
          onClose={closeSheet}
        />
      ) : null}
      <ConfirmDialog
        open={removing !== null}
        onOpenChange={(open) => {
          if (!open) setRemoving(null)
        }}
        heading={`Odebrat ${removingLabel} z foto a videa?`}
        description="V Mých dodavatelích zůstane. Zmizí jen odsud a jeho příjezd z harmonogramu."
        keepLabel="Ponechat"
        confirmLabel="Odebrat"
        onConfirm={() => {
          if (removing) removeNow(removing)
          setRemoving(null)
        }}
      />
    </Screen>
  )
}
